import React from "react";
import { Route, Switch } from "react-router-dom";

import * as QueriesQL from "queries/organization";
import * as Section from "@repoflow/components/section";
import { Crumb, useCrumbLabel } from "@repoflow/components/breadcrumb";

import * as ViewRoute from "./view";
import * as EditRoute from "./edit";
import * as RemoveRoute from "./remove";
import * as UsersRoute from "./users";
import * as KeysRoute from "./keys";

const LocalProvider = ({
  match: {
    params,
    params: { orgId },
  },
  children: Children,
}) => {
  const {
    base: parentBase,
    basepath: parentBasepath,
    schema,
    values: domain,
  } = Section.useSectionContext();

  const base = `${parentBase}/${orgId}`;
  const basepath = `${parentBasepath}/:orgId`;

  // Keyed on the entity, which is what the accidental remount used to do.
  //
  // Every screen below seeds its form state ONCE, from the entity this
  // route resolved. Until the render prop above was called rather than
  // rendered, React saw a new component type on every render and remounted
  // the lot — which reset those forms, and happened to be what made moving
  // from one orgId to another show the new one's values. It no longer
  // remounts for free, so the reset it was providing is asked for here.
  return (
    <Section.Provider
      key={orgId}
      schema={schema}
      base={base}
      basepath={basepath}
      attributes={{ ...params, domain }}
    >
      {/* Called, never rendered as an element — see Section.Provider. */}
      {Children({ base, basepath, params })}
    </Section.Provider>
  );
};

export const Content = (props) => {
  return (
    <LocalProvider {...props}>
      {({ params: { domain, orgId } }) => (
        <Section.Query
          query={QueriesQL.get}
          variables={{ domain, orgId }}
          datapath={"domains.get.organizations.get"}
        >
          {ContentHandler}
        </Section.Query>
      )}
    </LocalProvider>
  );
};

function ContentHandler({ basepath }) {
  return (
    <Switch>
      <Route path={`${basepath}/user`} component={UsersRoute.Content} />
      <Route path={`${basepath}/key`} component={KeysRoute.Content} />
      <Route path={`${basepath}/remove`} component={RemoveRoute.Content} />
      <Route path={`${basepath}/edit`} component={EditRoute.Content} />
      <Route path={`${basepath}/`} component={ViewRoute.Content} />
    </Switch>
  );
}

export const Sidebar = () => (
  <>
    <EditRoute.Sidebar />
    <RemoveRoute.Sidebar />
  </>
);

// The id is a hash and says nothing; the description is what the organization is
// called everywhere else on the page.
function OrganizationCrumb({ base, domain, orgId }) {
  const label = useCrumbLabel({
    query: QueriesQL.get,
    variables: { domain, orgId },
    datapath: "domains.get.organizations.get.description",
    fallback: orgId,
  });

  return <Crumb to={`${base}`} label={label} />;
}

export function Indice(props) {
  return (
    <LocalProvider {...props}>
      {({ base, basepath, params: { domain, orgId } }) => (
        <>
          <OrganizationCrumb base={base} domain={domain} orgId={orgId} />
          <Switch>
            <Route path={`${basepath}/user`} component={UsersRoute.Indice} />
            <Route path={`${basepath}/key`} component={KeysRoute.Indice} />
            <Route path={`${basepath}/edit`} component={EditRoute.Indice} />
            <Route path={`${basepath}/remove`} component={RemoveRoute.Indice} />
            <Route path={`${basepath}/`} component={ViewRoute.Indice} />
          </Switch>
        </>
      )}
    </LocalProvider>
  );
}
